import React, { useContext, useState } from "react";
import MobileContext from "context/MobileContext";
import { LeaseType, ListingStatus } from "types/globalTypes";
import cn from "classnames";
import { formatNumber } from "util/strings";

import SaveIcon from "components/Save/Icon";
import ListingBadge from "components/ListingBadge";
import generateSubtitle from "./GenerateSubTitle";
import renderPricing from "./RenderPricing";
import styles from "./style.module.scss";

const MID_DOT = "\u00B7";

const formatLeaseType = (leaseType: LeaseType | null) => {
  switch (leaseType) {
    case "SUBLEASE":
      return ` ${MID_DOT} Sublease`;
    case "DIRECT":
      return ` ${MID_DOT} Direct`;
    default:
      return "";
  }
};

interface IBuilding {
  id: string;
  image2?: { id: string; url: string } | null;
  address: string;
}

export interface IListing {
  id: string;
  accurateMonthlyPrice: number | null;
  accuratePsf: number | null;
  estimatedMonthlyPrice: string;
  estimatedPsf: number | null;
  displayMonthlyPrice: string | null;
  displayPSF: string | null;
  status: ListingStatus | null;
  building: IBuilding;
  image2?: { id: string; url: string } | null;
  brokerInsights: { id: string; content: string }[];
  squareFeet: number;
  estimatedSquareFeet: number;
  floorAndSuite: string;
  leaseType: LeaseType | null;
  maxOccupancy: number;
  estimatedMaxOccupancy: number;
}

interface IProps {
  listing: IListing;
  className?: string;
  signUpType: string;
  source: string;
  maxMonthlyPrice?: number;
  maxPricePerSquareFoot?: number;
  areaExternalIds: string[];
  marketOfInterest?: string | null;
  cityOfInterest?: string | null;
  leaseTermMin?: number | null;
  leaseTermMax?: number | null;
  minPricePerSquareFoot?: number | null;
  minSqft?: number | null;
  maxSqft?: number | null;
  minMonthlyPrice?: number | null;
  numEmployees?: string | null;
  phoneRequired?: boolean | null;
  onSuccess: (visitSpace: boolean) => void;
  showImage?: boolean;
  trackingEvent?: () => void;
}

const ListingCard = ({
  listing,
  className = "",
  signUpType,
  source,
  maxMonthlyPrice = 0,
  maxPricePerSquareFoot = 0,
  areaExternalIds,
  marketOfInterest,
  cityOfInterest,
  leaseTermMin,
  leaseTermMax,
  minPricePerSquareFoot,
  minSqft,
  maxSqft,
  minMonthlyPrice,
  numEmployees,
  phoneRequired,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSuccess = (visitSpace: boolean) => {},
  showImage = true,
  trackingEvent = () => {},
}: IProps) => {
  const isMobileView = useContext(MobileContext);
  const [showMap, setShowMap] = useState(false);
  const {
    building,
    brokerInsights,
    image2: listingImage,
    squareFeet,
    estimatedSquareFeet,
    floorAndSuite,
    leaseType,
    maxOccupancy,
    estimatedMaxOccupancy,
  } = listing;
  const { image2: buildingImage, address } = building;
  const brokerInsight =
    brokerInsights && brokerInsights.length > 0 && brokerInsights[0];
  const listingImageClasses = cn(styles.image, "card-image", {
    [styles.hideListingImage]: showMap,
  });

  return (
    <div className={cn(styles.card, className)}>
      {showImage && (
        <div className={styles.imageContainer}>
          {leaseType === "LICENSE_AGREEMENT" && (
            <ListingBadge badgeText="COWORKING" type="sfRecommended" />
          )}
          {(listingImage?.url || buildingImage?.url) && (
            <>
              <div className={listingImageClasses}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={listingImage?.url || buildingImage?.url}
                  alt={`Listing at ${address} - ${floorAndSuite}`}
                  loading="lazy"
                />
              </div>
              {!isMobileView && (
                <div className={styles.mapPin}>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src="/nextpublic/map-pin.svg"
                    alt="map-pin"
                    onMouseLeave={() => setShowMap(false)}
                    onMouseOver={() => setShowMap(true)}
                    onFocus={() => setShowMap(true)}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
      <div className={styles.inner}>
        <p className={`${styles.title}`}>
          {address} - {floorAndSuite}
        </p>
        <p className={styles.subtitle}>
          {generateSubtitle(building)}
          {formatLeaseType(leaseType)}
        </p>
        <p className={styles.pricing}>
          {renderPricing(listing, maxMonthlyPrice, maxPricePerSquareFoot)}
        </p>
        <p className={styles.details}>
          <i className="icon icon-layout" />
          <span className={styles.squarefeet}>
            {squareFeet
              ? formatNumber(squareFeet)
              : `~${formatNumber(estimatedSquareFeet)}`}{" "}
            sf
          </span>
          <i className="icon icon-user" />
          <span className={styles.maxOccupancy}>
            {maxOccupancy
              ? formatNumber(maxOccupancy)
              : `~${formatNumber(estimatedMaxOccupancy)}`}
          </span>
        </p>
        {brokerInsight && (
          <div className={styles.brokerInsightPreview}>
            <span>&quot;</span>
            <p className={styles.brokerInsightPara}>{brokerInsight.content}</p>
            <span>&quot;</span>
          </div>
        )}
      </div>
      {/* this stopPropagation event listener stops the click events
                occurring in the SaveIcon component from trigger the link it's
                nested in. */}
      {/* eslint-disable jsx-a11y/no-static-element-interactions */}
      <div
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        className={styles.saveIconContainer}
      >
        {/* eslint-enable jsx-a11y/no-static-element-interactions */}
        <SaveIcon
          id={listing.id}
          buildingId={listing.building.id}
          className={styles.saveIcon}
          signUpType={signUpType}
          source={source}
          areaExternalIds={areaExternalIds}
          marketOfInterest={marketOfInterest}
          leaseTermMin={leaseTermMin}
          leaseTermMax={leaseTermMax}
          minPricePerSquareFoot={minPricePerSquareFoot}
          maxPricePerSquareFoot={maxPricePerSquareFoot}
          minSqft={minSqft}
          maxSqft={maxSqft}
          minMonthlyPrice={minMonthlyPrice}
          maxMonthlyPrice={maxMonthlyPrice}
          numEmployees={numEmployees}
          phoneRequired={phoneRequired}
          onSuccess={onSuccess}
          cityOfInterest={cityOfInterest}
          trackingEvent={trackingEvent}
        />
      </div>
    </div>
  );
};

export default ListingCard;
